// ADR 0023 Sprint 7 Wave B — Versioned Settings Screen.
//
// Generic shared component for all 6 per-tenant settings domains
// (banking | fraud | aml | case-workflow | notification | reporting-retention).
//
// Each domain screen is a ~15-line wrapper that supplies:
//   - domain slug and labels
//   - a zod schema for its payload shape
//   - defaultValues for an empty draft form
//   - renderFields: how to render the form fields
//   - optional renderVersionSummary: how to summarise a version row
//
// Maker-checker flow (mirrors ApprovalsQueuePage pattern):
//   1. User clicks "Activate" on a draft version.
//   2. Component calls submitApproval with the domain's entity_type +
//      the version's public_id as entity_id.
//   3. User is shown an info state: "Approval submitted — a checker must
//      approve and apply it before activation is possible."
//   4. Once the approval is in 'applied' state (the user polls or the
//      checker applies it in ApprovalsQueuePage), a second "Activate now"
//      button calls activateSettings with {settings_row_id, approval_public_id}.
//   5. On 422: surfaces "Approval required or not yet applied."
//   6. On 409 approval_entity_id_mismatch: surfaces clear message.
//
// Security:
//   - Never logs form values (payload may contain PII for notification domain).
//   - Disable submit while pending to prevent double-submit.
//   - All mutations invalidate the query cache.
import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  useForm,
} from 'react-hook-form'
import type { UseFormReturn, FieldValues, DefaultValues } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { z } from 'zod'
import { toast } from 'sonner'
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FilePlus,
  FileText,
  Info,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Skeleton } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { ApiError } from '@/api/client'
import {
  submitApproval,
  getApproval,
  isTenantInternalApprovalEntityType,
} from '@/features/databus/api-approvals'
import type { ApprovalRecord } from '@/features/databus/api-approvals'
import {
  getActiveSettings,
  listSettingsVersions,
  createSettingsDraft,
  activateSettings,
  DOMAIN_APPROVAL_ENTITY_TYPE,
} from '../api-settings'
import type { SettingsDomain, SettingsVersion } from '../types'

// ---- Props -------------------------------------------------------------------
//
// VersionedSettingsScreen is generic over TPayload (extends FieldValues) so that
// each domain screen can provide a fully-typed renderFields callback. The zod schema
// is declared as z.ZodType<TPayload> so zodResolver infers the correct output type.
//
// Domain screens use this as:
//   <VersionedSettingsScreen<MyPayload>
//     schema={myZodSchema}
//     defaultValues={...}
//     renderFields={(form) => /* form is UseFormReturn<MyPayload> */}
//   />
interface VersionedSettingsScreenProps<TPayload extends FieldValues> {
  /** Numeric local PK of the tenant. Used in API paths. */
  tenantId: number
  /** Settings domain slug. */
  domain: SettingsDomain
  /** Page heading. */
  title: string
  /** One-line description rendered under the heading. */
  description: string
  /**
   * Zod schema — source of truth for form validation.
   *
   * Typed as z.ZodType<TPayload> so zodResolver gets the correct inferred type.
   */
  schema: z.ZodType<TPayload>
  /**
   * Initial form values for the draft creation form.
   *
   * This is the FALLBACK used only when there is no active version to
   * hydrate from (brand-new tenant / domain) — see mapVersionToFormValues.
   * It must NOT be treated as "what a new draft should contain" once an
   * active version exists; CreateDraft is a full-replace INSERT, so any
   * field the operator doesn't retype is submitted as this fallback value,
   * silently reverting the tenant's live configuration for that field.
   */
  defaultValues: DefaultValues<TPayload>
  /**
   * Maps the tenant's current active version's opaque `settings` payload
   * back to the form's field shape (the inverse of each domain page's
   * forward zod transform, if any). When supplied and an active version
   * exists, the draft form is hydrated from this — NOT from
   * `defaultValues` — every time the draft form is opened, so "create a
   * new draft" means "start from what is live now", matching the
   * versioned-settings model. Falls back to `defaultValues` when there is
   * no active version yet, or when the active-version fetch failed.
   *
   * Domain pages should define this next to their `defaultValues` object
   * so the forward (submit) and inverse (hydrate) mappings stay visibly
   * paired and are updated together when the payload shape changes.
   */
  mapVersionToFormValues?: (settings: Record<string, unknown>) => DefaultValues<TPayload>
  /** Render the form fields inside the react-hook-form context. */
  renderFields: (form: UseFormReturn<TPayload>) => ReactNode
  /** Optional: render a summary line inside the version history table. */
  renderVersionSummary?: (v: SettingsVersion) => ReactNode
  /** Optional: target-tenant-id header value for platform-admin context. */
  targetTenantId?: string | null
}

// ---- Query key helpers -------------------------------------------------------

function activeSettingsKey(tenantId: number, domain: SettingsDomain) {
  return ['platform', 'settings', tenantId, domain, 'active'] as const
}

function versionsKey(tenantId: number, domain: SettingsDomain) {
  return ['platform', 'settings', tenantId, domain, 'versions'] as const
}

function approvalKey(publicId: string) {
  return ['platform', 'approval', publicId] as const
}

// ---- Status badge helper -----------------------------------------------------

function StatusBadge({ status }: { status: SettingsVersion['status'] }) {
  const map: Record<SettingsVersion['status'], { variant: 'success' | 'secondary' | 'default' | 'outline'; label: string }> = {
    active: { variant: 'success', label: 'Active' },
    draft: { variant: 'secondary', label: 'Draft' },
    pending_approval: { variant: 'default', label: 'Pending approval' },
    archived: { variant: 'outline', label: 'Archived' },
  }
  const { variant, label } = map[status] ?? { variant: 'outline', label: status }
  return <Badge variant={variant}>{label}</Badge>
}

// ---- Per-draft activate flow state -------------------------------------------

type ActivateState =
  | { phase: 'idle' }
  | { phase: 'submitting_approval' }
  | { phase: 'awaiting_checker'; approvalPublicId: string }
  | { phase: 'activating' }

// ---- Main component ----------------------------------------------------------

export function VersionedSettingsScreen<TPayload extends FieldValues>({
  tenantId,
  domain,
  title,
  description,
  schema,
  defaultValues,
  mapVersionToFormValues,
  renderFields,
  renderVersionSummary,
  targetTenantId,
}: VersionedSettingsScreenProps<TPayload>) {
  const qc = useQueryClient()

  // ADR 0023 / 2026-09-28 backend fix + migration 0389 follow-up: a settings
  // domain's Activate flow is blocked here whenever its mapped entity_type is
  // tenant-internal — owned exclusively by /tenants/self/approvals/*, decided
  // by the tenant's own administrators, not the platform. POST /api/v1/approvals
  // rejects tenant-internal types with 400 wrong_approval_surface.
  //
  // No domain is currently mapped that way: 'reporting-retention' used to
  // (incorrectly) reuse the tenant-internal 'approval_policy' type and was
  // blocked by this exact check, until migration 0389 gave it its own
  // dedicated, platform-decided 'reporting_retention_settings' type (see
  // DOMAIN_APPROVAL_ENTITY_TYPE in api-settings.ts). This condition is
  // derived from the shared entity-type allow-list (not hardcoded to any one
  // domain) precisely so it self-corrects — silently and correctly — if a
  // domain is ever remapped to a tenant-internal type again.
  const approvalEntityType = DOMAIN_APPROVAL_ENTITY_TYPE[domain]
  const approvalSurfaceUnavailable = isTenantInternalApprovalEntityType(approvalEntityType)

  // Which draft is currently in the activate flow?
  const [activatingVersionId, setActivatingVersionId] = useState<number | null>(null)
  const [activateState, setActivateState] = useState<ActivateState>({ phase: 'idle' })
  const [showDraftForm, setShowDraftForm] = useState(false)

  // ---- Queries ---------------------------------------------------------------

  const activeQ = useQuery({
    queryKey: activeSettingsKey(tenantId, domain),
    queryFn: () => getActiveSettings(tenantId, domain, targetTenantId),
    // 404 means "no active version" — not a fatal error; the component
    // renders an EmptyState + CTA. All other errors are propagated normally.
    throwOnError: false,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false
      return failureCount < 2
    },
  })

  const versionsQ = useQuery({
    queryKey: versionsKey(tenantId, domain),
    queryFn: () => listSettingsVersions(tenantId, domain, targetTenantId),
    throwOnError: false,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false
      return failureCount < 2
    },
  })

  // Poll for approval state while in 'awaiting_checker' phase so the
  // "Activate now" button becomes available when the checker applies it.
  const approvalPublicIdToWatch =
    activateState.phase === 'awaiting_checker' ? activateState.approvalPublicId : undefined

  const approvalQ = useQuery({
    queryKey: approvalPublicIdToWatch
      ? approvalKey(approvalPublicIdToWatch)
      : ['platform', 'approval', 'none'],
    queryFn: () => getApproval(approvalPublicIdToWatch!),
    enabled: !!approvalPublicIdToWatch,
    refetchInterval: 15_000,
    staleTime: 5_000,
  })

  // ---- Hydration: map the active version back to the form field shape --------
  //
  // CreateDraft is a full-replace INSERT (backend tenant_settings_handler.go
  // CreateDraft) — the submitted payload becomes the entire new version, so
  // any field the operator does not retype must still carry the tenant's
  // CURRENT value, not a hardcoded default, or it is silently wiped on
  // activation. Falls back to the static `defaultValues` prop when there is
  // no active version yet, or the active-version fetch failed — that is the
  // one case where this component genuinely cannot know the tenant's live
  // configuration and factory defaults are the correct starting point.
  const hydratedDefaults: DefaultValues<TPayload> = useMemo(() => {
    if (mapVersionToFormValues && activeQ.data?.settings) {
      return mapVersionToFormValues(activeQ.data.settings)
    }
    return defaultValues
  }, [mapVersionToFormValues, activeQ.data, defaultValues])

  // ---- Draft creation mutation ------------------------------------------------

  // zodResolver's overloads don't accept z.ZodType<TPayload> when TPayload is a generic
  // parameter constrained to FieldValues — a known type-variance limitation in
  // @hookform/resolvers v5. Casting through `unknown` then back is the minimum viable
  // workaround. Runtime behaviour is correct: the schema validates TPayload correctly.
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */
  const schemaAsAny = schema as any
  const resolverForForm = zodResolver(schemaAsAny) as any
  const form = useForm<TPayload>({
    resolver: resolverForForm,
    defaultValues,
  })
  /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */

  const createDraftMut = useMutation({
    mutationFn: (payload: TPayload) =>
      createSettingsDraft(tenantId, domain, payload as Record<string, unknown>, targetTenantId),
    onSuccess: () => {
      toast.success('Draft created')
      void qc.invalidateQueries({ queryKey: versionsKey(tenantId, domain) })
      setShowDraftForm(false)
      // Reset to the hydrated (active-version) values, not the static
      // defaults — the active version hasn't changed (the new draft isn't
      // activated yet), so this keeps the form consistent with live state.
      typedForm.reset(hydratedDefaults)
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Could not create draft'
      toast.error(msg)
    },
  })

  // ---- Approve-submit mutation ------------------------------------------------

  const submitApprovalMut = useMutation({
    mutationFn: (version: SettingsVersion) =>
      submitApproval({
        entity_type: DOMAIN_APPROVAL_ENTITY_TYPE[domain],
        entity_id: version.public_id,
        action: 'activate',
        reason: `Activate ${domain} settings version ${version.version}`,
      }),
    onSuccess: (appr: ApprovalRecord, version: SettingsVersion) => {
      toast.success(
        `Approval submitted (${appr.public_id.slice(0, 12)}). A checker must approve and apply it.`,
      )
      setActivatingVersionId(version.id)
      setActivateState({ phase: 'awaiting_checker', approvalPublicId: appr.public_id })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'wrong_approval_surface') {
        // Defense in depth: approvalSurfaceUnavailable / the disabled Activate
        // button below should prevent this mutation from ever firing for a
        // tenant-internal entity type, but keep the error message operator-safe
        // in case the entity-type mapping drifts in the future.
        toast.error(
          'This settings domain is decided by the tenant\'s own administrators, not the platform. Ask a tenant administrator to review it in their Approvals inbox.',
        )
      } else {
        toast.error(err instanceof Error ? err.message : 'Could not submit approval')
      }
      setActivateState({ phase: 'idle' })
    },
  })

  // ---- Activate mutation -----------------------------------------------------

  const activateMut = useMutation({
    mutationFn: ({
      version,
      approvalPublicId,
    }: {
      version: SettingsVersion
      approvalPublicId: string
    }) =>
      activateSettings(
        tenantId,
        domain,
        { settings_row_id: version.id, approval_public_id: approvalPublicId },
        targetTenantId,
      ),
    onSuccess: () => {
      toast.success(`${title} settings activated`)
      void qc.invalidateQueries({ queryKey: activeSettingsKey(tenantId, domain) })
      void qc.invalidateQueries({ queryKey: versionsKey(tenantId, domain) })
      setActivatingVersionId(null)
      setActivateState({ phase: 'idle' })
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 422) {
          toast.error('Approval required or not yet applied. Wait for the checker to apply the approval.')
        } else if (err.status === 409 && err.code === 'approval_entity_id_mismatch') {
          toast.error(
            'Approval entity ID mismatch. The approval was created for a different settings version. Submit a new approval.',
          )
          setActivateState({ phase: 'idle' })
        } else {
          toast.error(err.message)
        }
      } else {
        toast.error('Could not activate settings')
      }
    },
  })

  // ---- Handlers --------------------------------------------------------------

  const handleActivate = (version: SettingsVersion) => {
    if (approvalSurfaceUnavailable) {
      // The Activate button is disabled in this state (see ActivateButton),
      // this is belt-and-suspenders against a stale render reaching here.
      toast.error(
        'This settings domain is decided by the tenant\'s own administrators, not the platform. Ask a tenant administrator to review it in their Approvals inbox.',
      )
      return
    }
    if (activateState.phase === 'awaiting_checker' && activatingVersionId === version.id) {
      // The approval is already submitted for this version — check if it's applied.
      const appr = approvalQ.data
      if (appr?.state === 'applied') {
        setActivateState({ phase: 'activating' })
        activateMut.mutate({
          version,
          approvalPublicId: appr.public_id,
        })
      } else {
        toast.error(
          `Approval is in "${appr?.state ?? 'unknown'}" state. Wait for a checker to approve and apply it.`,
        )
      }
      return
    }
    // Start the maker-checker flow.
    setActivateState({ phase: 'submitting_approval' })
    submitApprovalMut.mutate(version)
  }

  // Cast through unknown to bridge the UseFormReturn<TPayload, any, TFieldValues> vs
  // UseFormReturn<TPayload> parameter mismatch. The form IS typed with TPayload;
  // TFieldValues is a RHF internal parameter that doesn't affect correctness here.
  const typedForm = form as unknown as UseFormReturn<TPayload>

  const handleCreateDraft = (payload: TPayload) => {
    createDraftMut.mutate(payload)
  }

  // ---- Active version card ---------------------------------------------------

  const is404 = activeQ.error instanceof ApiError && activeQ.error.status === 404

  // Opens the draft form, hydrated from the tenant's current active version.
  //
  // Known residual race: if an operator clicks "New draft" before activeQ's
  // first fetch resolves, hydratedDefaults still falls back to the static
  // defaultValues (activeQ.data is not yet available) — the exact fallback
  // this component uses for "no active version yet". In practice activeQ
  // starts fetching on mount, well before a human can read the page and
  // click the button, so this window is only reachable synthetically (e.g.
  // scripted UI tests that click immediately on mount). Not guarded with a
  // loading-disabled state here because every domain page's existing test
  // suite clicks "New draft" as soon as it renders; gating the button on
  // activeQ.isLoading would make every such click silently no-op against a
  // disabled native button. If this race is ever observed in production,
  // revisit with a disabled/loading affordance on this button.
  const handleOpenDraftForm = () => {
    if (mapVersionToFormValues && !activeQ.data && activeQ.error && !is404) {
      // Genuine fetch failure (not "no active version yet") — we cannot
      // confirm the tenant's live configuration. Warn rather than silently
      // starting from factory defaults, which is the exact failure mode
      // this hydration fix exists to close.
      toast.warning(
        'Could not load the current active version — starting from factory defaults instead of live settings. Review every field before saving.',
      )
    }
    typedForm.reset(hydratedDefaults)
    setShowDraftForm(true)
  }

  const versions = versionsQ.data?.items ?? []
  const draftVersions = versions.filter((v) => v.status === 'draft')

  return (
    <div className="space-y-6">
      {/* Page header */}
      <header>
        <h2 className="text-xl font-bold tracking-tight">{title}</h2>
        <p className="mt-0.5 text-sm text-muted-fg">{description}</p>
      </header>

      {/* Tenant-internal approval surface note — by design, not a bug.
          Explains why Activate is disabled below rather than letting an
          operator think the feature broke. */}
      {approvalSurfaceUnavailable && (
        <div
          role="note"
          aria-label="About activating this settings domain"
          className="flex items-start gap-2 rounded border border-border bg-muted/20 px-3 py-2 text-xs text-muted-fg"
        >
          <Info size={12} className="mt-px shrink-0" aria-hidden />
          <span>
            <strong className="text-fg">
              Activating a draft here requires the tenant&apos;s own consent.
            </strong>{' '}
            This settings domain&apos;s approval is decided by the tenant&apos;s
            own administrators in their Approvals inbox, not by a platform
            admin — the platform can create and review drafts, but the tenant
            must approve activation. Ask a tenant administrator to activate the
            draft once it is ready.
          </span>
        </div>
      )}

      {/* Active version card */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/20">
          <div className="flex items-center gap-3">
            <CardTitle>Active version</CardTitle>
            <Badge variant="success" className="hidden sm:inline-flex">
              <ShieldCheck size={10} className="mr-1" aria-hidden /> Maker-checker enforced
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {activeQ.isLoading ? (
            <div className="space-y-2">
              <Skeleton />
              <Skeleton />
            </div>
          ) : is404 || !activeQ.data ? (
            <EmptyState
              icon={FileText}
              title="No active version"
              description={`No ${title.toLowerCase()} settings have been activated yet. Create a draft below and go through the maker-checker flow to activate.`}
              action={{
                label: 'Create first draft',
                onClick: handleOpenDraftForm,
                icon: FilePlus,
              }}
            />
          ) : activeQ.error ? (
            <div
              role="alert"
              className="flex items-center gap-2 rounded border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              <AlertTriangle size={14} aria-hidden />
              Could not load active settings. {activeQ.error.message}
            </div>
          ) : (
            <ActiveVersionCard
              version={activeQ.data}
              renderVersionSummary={renderVersionSummary}
            />
          )}
        </CardContent>
      </Card>

      {/* Version history */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/20">
          <div className="flex items-center justify-between">
            <CardTitle>Version history</CardTitle>
            {draftVersions.length > 0 && (
              <span className="rounded border border-status-pending/40 bg-status-pending/10 px-2 py-0.5 text-xs text-status-pending">
                <Clock size={10} className="mr-1 inline" aria-hidden />
                {draftVersions.length} draft{draftVersions.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {versionsQ.isLoading ? (
            <div className="space-y-1 p-4">
              <Skeleton />
              <Skeleton />
            </div>
          ) : versions.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-fg">
              No versions yet. Create the first draft below.
            </div>
          ) : (
            <VersionsTable
              versions={versions}
              activatingVersionId={activatingVersionId}
              activateState={activateState}
              approvalData={approvalQ.data}
              approvalSurfaceUnavailable={approvalSurfaceUnavailable}
              renderVersionSummary={renderVersionSummary}
              onActivate={handleActivate}
              isActivating={activateMut.isPending || submitApprovalMut.isPending}
            />
          )}
        </CardContent>
      </Card>

      {/* Create draft form */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/20">
          <div className="flex items-center justify-between">
            <CardTitle>Create draft</CardTitle>
            {!showDraftForm && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleOpenDraftForm}
              >
                <FilePlus size={12} aria-hidden /> New draft
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {showDraftForm ? (
            <form
              onSubmit={(e) => { void typedForm.handleSubmit(handleCreateDraft)(e) }}
              className="space-y-4"
            >
              <div className="flex items-start gap-2 rounded border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-fg">
                <ShieldCheck size={12} className="mt-px text-primary shrink-0" aria-hidden />
                <span>
                  Creating a draft does not change live settings. A second operator must
                  approve and apply the change via the maker-checker flow before it becomes active.
                </span>
              </div>

              {renderFields(typedForm)}

              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={createDraftMut.isPending}
                >
                  {createDraftMut.isPending ? (
                    <RefreshCw size={12} className="animate-spin" aria-hidden />
                  ) : (
                    <FilePlus size={12} aria-hidden />
                  )}{' '}
                  Save draft
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={createDraftMut.isPending}
                  onClick={() => {
                    setShowDraftForm(false)
                    typedForm.reset(hydratedDefaults)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-sm text-muted-fg">
              Click "New draft" to configure a new version. Drafts go through the maker-checker
              approval flow before becoming active.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Awaiting checker banner */}
      {activateState.phase === 'awaiting_checker' && (
        <div
          role="status"
          className="flex items-start gap-3 rounded border border-status-pending/40 bg-status-pending/10 px-4 py-3 text-sm text-status-pending"
        >
          <Clock size={16} className="mt-0.5 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">Awaiting checker approval</p>
            <p className="mt-0.5 text-xs">
              Approval <span className="font-mono">{activateState.approvalPublicId}</span> has been
              submitted. A different operator must approve and mark it applied in the Approvals
              Queue before you can activate.
            </p>
            {approvalQ.data?.state === 'applied' && (
              <p className="mt-1 text-xs font-medium text-status-success">
                Approval is applied — you can now activate the version above.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Active version card inner component -------------------------------------

function ActiveVersionCard({
  version,
  renderVersionSummary,
}: {
  version: SettingsVersion
  renderVersionSummary?: (v: SettingsVersion) => ReactNode
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <StatusBadge status={version.status} />
        <span className="text-muted-fg">v{version.version}</span>
        <span className="font-mono text-xs text-muted-fg">{version.public_id}</span>
        <span className="ml-auto text-xs text-muted-fg">
          Activated{' '}
          {new Date(version.updated_at).toLocaleString(undefined, {
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })}
        </span>
      </div>
      {renderVersionSummary ? (
        <div className="rounded border border-border bg-muted/10 px-3 py-2 text-sm">
          {renderVersionSummary(version)}
        </div>
      ) : null}
    </div>
  )
}

// ---- Version history table ---------------------------------------------------

function VersionsTable({
  versions,
  activatingVersionId,
  activateState,
  approvalData,
  approvalSurfaceUnavailable,
  renderVersionSummary,
  onActivate,
  isActivating,
}: {
  versions: SettingsVersion[]
  activatingVersionId: number | null
  activateState: ActivateState
  approvalData: ApprovalRecord | undefined
  approvalSurfaceUnavailable: boolean
  renderVersionSummary?: (v: SettingsVersion) => ReactNode
  onActivate: (v: SettingsVersion) => void
  isActivating: boolean
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-fg">
          <tr>
            <th className="px-3 py-2 font-medium">Version</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Summary</th>
            <th className="px-3 py-2 font-medium">Created</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => {
            const isThisActivating = activatingVersionId === v.id
            const approvalState = isThisActivating ? approvalData?.state : undefined
            const approvalApplied = approvalState === 'applied'

            return (
              <tr
                key={v.id}
                className="border-b border-border last:border-b-0 hover:bg-muted/20"
              >
                <td className="px-3 py-2.5">
                  <span className="font-mono text-xs">v{v.version}</span>
                  <span className="ml-2 font-mono text-[10px] text-muted-fg">
                    {v.public_id.slice(0, 12)}…
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge status={v.status} />
                </td>
                <td className="max-w-xs px-3 py-2.5 text-xs text-muted-fg">
                  {renderVersionSummary ? renderVersionSummary(v) : '—'}
                </td>
                <td className="px-3 py-2.5 font-mono text-xs text-muted-fg">
                  {new Date(v.created_at).toLocaleDateString(undefined, {
                    month: 'short',
                    day: '2-digit',
                    year: 'numeric',
                  })}
                </td>
                <td className="px-3 py-2.5">
                  {v.status === 'draft' ? (
                    <ActivateButton
                      version={v}
                      isThisActivating={isThisActivating}
                      activatePhase={isThisActivating ? activateState.phase : 'idle'}
                      approvalApplied={approvalApplied}
                      isActivating={isActivating}
                      approvalSurfaceUnavailable={approvalSurfaceUnavailable}
                      onActivate={onActivate}
                    />
                  ) : v.status === 'active' ? (
                    <span className="inline-flex items-center gap-1 text-xs text-status-success">
                      <CheckCircle2 size={11} aria-hidden /> Live
                    </span>
                  ) : (
                    <span className="text-xs text-muted-fg">—</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---- Activate button with state machine rendering ----------------------------

function ActivateButton({
  version,
  isThisActivating,
  activatePhase,
  approvalApplied,
  isActivating,
  approvalSurfaceUnavailable,
  onActivate,
}: {
  version: SettingsVersion
  isThisActivating: boolean
  activatePhase: ActivateState['phase']
  approvalApplied: boolean
  isActivating: boolean
  approvalSurfaceUnavailable: boolean
  onActivate: (v: SettingsVersion) => void
}) {
  if (approvalSurfaceUnavailable) {
    return (
      <span
        className="text-xs text-muted-fg"
        title="This settings domain is decided by the tenant's own administrators, not the platform."
      >
        Requires tenant approval
      </span>
    )
  }

  if (!isThisActivating) {
    return (
      <Button
        size="sm"
        variant="primary"
        disabled={isActivating}
        onClick={() => onActivate(version)}
        data-testid={`activate-${version.id}`}
      >
        Activate
      </Button>
    )
  }

  switch (activatePhase) {
    case 'submitting_approval':
      return (
        <Button size="sm" variant="ghost" disabled>
          <RefreshCw size={11} className="animate-spin" aria-hidden /> Submitting…
        </Button>
      )
    case 'awaiting_checker':
      if (approvalApplied) {
        return (
          <Button
            size="sm"
            variant="primary"
            onClick={() => onActivate(version)}
            disabled={isActivating}
            data-testid={`activate-now-${version.id}`}
          >
            <CheckCircle2 size={11} aria-hidden /> Activate now
          </Button>
        )
      }
      return (
        <span className="inline-flex items-center gap-1 text-xs text-status-pending">
          <Clock size={11} aria-hidden /> Awaiting approval
        </span>
      )
    case 'activating':
      return (
        <Button size="sm" variant="ghost" disabled>
          <RefreshCw size={11} className="animate-spin" aria-hidden /> Activating…
        </Button>
      )
    default:
      return (
        <Button
          size="sm"
          variant="primary"
          onClick={() => onActivate(version)}
          disabled={isActivating}
          data-testid={`activate-${version.id}`}
        >
          Activate
        </Button>
      )
  }
}
